/**
 * Column — Eine Kanban-Spalte (BOARD-01, BOARD-05, BOARD-06).
 *
 * Droppable via @dnd-kit/core useDroppable. Beim Hover ueber die Spalte
 * wird der Hintergrund leicht gelblich, um das Drop-Target zu zeigen.
 *
 * Header: Spaltenname + Count-Badge.
 * Spezial nur in "In Kontakt": zusaetzlicher Plus-Button im Header
 *   (BOARD-06, oeffnet CreateLeadModal via store.openCreateModal).
 * Body: sortierte Karten via sortLeadsForColumn(...).
 * Empty: "Keine Leads"-Hinweis.
 *
 * CI-Farben: #E9FF57 / #1A1A1A / #DAD9D9.
 */

import { useDroppable } from '@dnd-kit/core';
import { Plus } from 'lucide-react';
import type { Lead } from '../types';
import type { ColumnDef } from '../lib/columns';
import { sortLeadsForColumn } from '../lib/sortLeads';
import { useLeadBoard } from '../store/useLeadBoard';
import Card from './Card';

interface ColumnProps {
  column: ColumnDef;
  leads: Lead[];
  now: Date;
}

export default function Column({ column, leads, now }: ColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${column.status}`,
    data: { status: column.status },
  });

  const openCreateModal = useLeadBoard((s) => s.openCreateModal);
  const sorted = sortLeadsForColumn(leads, column.status);
  const isInKontakt = column.status === 'in_kontakt';

  return (
    <div
      ref={setNodeRef}
      className={
        'flex flex-col flex-shrink-0 min-w-[260px] max-w-[340px] w-[280px] rounded p-2 transition-colors ' +
        (isOver ? 'bg-[#E9FF57]/10' : 'bg-gray-50')
      }
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2 px-1 sticky top-0 z-10 bg-inherit">
        <span className="text-sm font-semibold text-[#1A1A1A]">
          {column.label}
        </span>
        <div className="flex items-center gap-2">
          <span className="bg-[#DAD9D9] text-[#1A1A1A] px-2 py-0.5 rounded-full text-xs">
            {sorted.length}
          </span>
          {isInKontakt && (
            <button
              type="button"
              onClick={openCreateModal}
              className="bg-[#E9FF57] text-[#1A1A1A] w-6 h-6 rounded-full flex items-center justify-center hover:opacity-90"
              title="Manuellen Lead anlegen"
              aria-label="Manuellen Lead anlegen"
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto pr-1 min-h-[200px]">
        {sorted.length === 0 ? (
          <div className="text-xs text-gray-400 text-center py-4">Keine Leads</div>
        ) : (
          sorted.map((lead) => <Card key={lead.id} lead={lead} now={now} />)
        )}
      </div>
    </div>
  );
}
